import { api } from "@/lib/api";

const listFrom = (value, keys = []) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  for (const key of keys) {
    if (Array.isArray(value[key])) return value[key];
  }
  return [];
};

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

const prettifyToken = (value) =>
  String(value || "")
    .replace(/^feeder:/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const prettifyEventType = (value, fallback = "Event") => {
  const cleaned = String(value || "")
    .trim()
    .replace(/[_-]+/g, " ");
  if (!cleaned) return fallback;
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
};

const asOwnerParty = (value) => {
  if (!value) return "joint";
  const raw = String(value).toLowerCase();
  if (raw === "a" || raw === "david" || raw === "operator_a") return "A";
  if (raw === "b" || raw === "maryse" || raw === "operator_b") return "B";
  if (raw === "joint") return "joint";
  return value;
};

const cleanAction = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s/]+/g, "-")
    .replace(/_/g, "-");

const laneFromState = (value) => {
  const state = cleanAction(value);
  if (["needs-you", "needs-action", "action-needed", "open", "pending", "due", "active"].includes(state)) return "needs_you";
  if (["waiting-on-them", "waiting", "reply-sent", "awaiting-reply", "follow-up-sent"].includes(state)) return "waiting_on_them";
  if (["gated", "snoozed", "scheduled", "paused", "deferred"].includes(state)) return "gated";
  if (["void", "voided"].includes(state)) return "voided";
  if (["merge", "merged"].includes(state)) return "merged";
  if (["handled", "done", "complete", "completed", "closed"].includes(state)) return "handled";
  return "needs_you";
};

const feederOriginLabel = (underRule) => {
  const key = String(underRule || "")
    .trim()
    .toLowerCase();
  if (!key) return "Proposed from a feeder";
  const feederName = {
    "feeder:postal": "postal mail",
    "feeder:mail": "postal mail",
    "feeder:email": "email",
    "feeder:gmail": "email",
    "feeder:sms": "text message",
    "feeder:text": "text message",
    "feeder:bank": "bank activity",
    "feeder:calendar": "calendar",
    "feeder:portal": "portal activity",
  }[key];
  return `Proposed from ${feederName || prettifyToken(key).toLowerCase()}`;
};

const statusMeta = (lane, rawState) => {
  const label = prettifyEventType(rawState, "Needs action");
  if (lane === "waiting_on_them") return { label, tone: "neutral", icon: "clock" };
  if (lane === "gated") return { label, tone: "neutral", icon: "gate" };
  if (lane === "voided") return { label: "Voided", tone: "late", icon: "void" };
  if (lane === "merged") return { label: "Merged", tone: "neutral", icon: "merge" };
  if (lane === "handled") return { label, tone: "positive", icon: "check" };
  return { label, tone: "attention", icon: "attention" };
};

const normalizeProvenance = (item) => {
  const source = item?.provenance || item?.source || item?.origin || {};
  const feederRule = firstDefined(
    item?.under_rule,
    item?.source_under_rule,
    item?.source_payload?.under_rule,
    item?.payload?.under_rule,
    source?.under_rule
  );
  const channel = firstDefined(
    feederRule ? "feeder" : undefined,
    source.channel,
    item?.source_channel,
    item?.channel,
    item?.provenance_channel,
    item?.capture_source,
    "capture"
  );
  const origin = firstDefined(
    feederRule ? feederOriginLabel(feederRule) : undefined,
    source.origin,
    source.label,
    source.title,
    item?.source_label,
    item?.origin,
    item?.counterparty,
    item?.source_name,
    "Your capture"
  );
  const timestamp = firstDefined(
    item?.ts,
    source.timestamp,
    source.occurred_at,
    source.created_at,
    item?.source_at,
    item?.created_at,
    item?.updated_at
  );
  const detail = firstDefined(source.detail, item?.source_detail, item?.provenance_detail);
  return { channel, origin, timestamp, detail, feederRule };
};

const normalizeEvent = (event) => {
  const timestamp = firstDefined(event?.ts, event?.timestamp, event?.created_at, event?.occurred_at, event?.at, event?.time);
  const rawType = firstDefined(event?.type, event?.kind, event?.label, event?.action, "Event");
  const seq = firstDefined(event?.seq, event?.sequence, event?.position);
  const snippet = firstDefined(
    event?.snippet,
    event?.summary,
    event?.text,
    event?.raw_text,
    event?.body,
    event?.payload?.text,
    event?.payload?.body,
    event?.payload?.summary
  );
  return {
    id: firstDefined(event?.id, seq !== undefined ? `event-${seq}` : `${rawType}-${timestamp || Math.random().toString(36).slice(2)}`),
    icon: firstDefined(event?.icon, event?.channel, event?.kind, "event"),
    timestamp,
    type: prettifyEventType(rawType),
    rawType,
    seq: seq === undefined || seq === null || seq === "" ? null : Number(seq),
    snippet,
    payload: event?.payload || null,
    raw: event,
    sourceHref: firstDefined(event?.href, event?.url),
    sourceLabel: firstDefined(event?.link_label, event?.source_label),
  };
};

const fallbackEvents = (obligation) => {
  const events = [];
  if (obligation?.rawText) {
    events.push({
      id: `${obligation.id}-capture`,
      icon: obligation?.provenance?.channel || "capture",
      timestamp: obligation?.provenance?.timestamp || obligation?.createdAt,
      type: "Captured note",
      rawType: "captured_note",
      seq: 0,
      snippet: obligation.rawText,
      payload: { raw_text: obligation.rawText },
    });
  }
  if (obligation?.provenance?.origin || obligation?.provenance?.detail) {
    events.push({
      id: `${obligation.id}-source`,
      icon: obligation?.provenance?.channel || "source",
      timestamp: obligation?.provenance?.timestamp || obligation?.createdAt,
      type: obligation?.provenance?.origin || "Source detail",
      rawType: "source_detail",
      seq: 1,
      snippet: obligation?.provenance?.detail,
      payload: obligation?.sourcePayload || null,
    });
  }
  return events;
};

export const normalizeObligation = (item) => {
  const rawState = firstDefined(item?.derived_state, item?.state, item?.status, item?.lane_label, "Needs action");
  const lane = laneFromState(rawState);
  const title = firstDefined(item?.title, item?.subject, item?.name, item?.headline, item?.raw_text, "Untitled obligation");
  const counterparty = firstDefined(item?.counterparty, item?.contact_name, item?.source_party, item?.from, item?.vendor_name);
  const asset = firstDefined(item?.asset_label, item?.asset_name, item?.property_name, item?.address, item?.context);
  const provenance = normalizeProvenance(item);
  const rawText = firstDefined(item?.capture_raw_text, item?.raw_text, item?.source_text, item?.notes);
  const events = listFrom(item, ["events", "history", "proof_trail", "timeline"]).map((event) => normalizeEvent(event));

  return {
    id: firstDefined(item?.id, item?.obligation_id, item?.uuid),
    title,
    counterparty,
    asset,
    ownerParty: asOwnerParty(firstDefined(item?.owner_party, item?.owner, item?.assigned_to, "joint")),
    ownerLabel: firstDefined(item?.owner_label, item?.owner_name),
    amount: firstDefined(item?.amount, item?.amount_due, item?.estimated_amount, item?.value),
    dueDate: firstDefined(item?.due_date, item?.due_at, item?.deadline),
    gateDate: firstDefined(item?.gate_date, item?.snooze_until, item?.wait_until, item?.return_on),
    rawState,
    lane,
    status: statusMeta(lane, rawState),
    provenance,
    rawText,
    createdAt: firstDefined(item?.created_at, item?.inserted_at),
    updatedAt: firstDefined(item?.updated_at, item?.modified_at),
    captureId: firstDefined(item?.capture_id, item?.pending_capture_id),
    suggestionId: firstDefined(item?.suggestion_id),
    evidenceRefs: listFrom(item, ["evidence_refs", "references", "attachments", "docs"]),
    sourcePayload: firstDefined(item?.source_payload, item?.payload, item?.evidence),
    auditLabel: lane === "voided" ? "Voided" : lane === "merged" ? "Merged" : null,
    events: events.length ? events : fallbackEvents({ id: item?.id, rawText, provenance, createdAt: item?.created_at, sourcePayload: item?.source_payload }),
    raw: item,
  };
};

export const normalizeCandidate = (item) => {
  const payload = item?.payload || {};
  const classification = String(firstDefined(payload?.classification, item?.classification, "obligation")).toLowerCase();
  const title = firstDefined(payload?.title, item?.title, "Untitled candidate");
  const ownerRaw = firstDefined(payload?.owner, item?.owner, "joint");
  const counterparty = firstDefined(payload?.counterparty, item?.counterparty);
  const gateDate = firstDefined(payload?.gate_date, item?.gate_date);
  const amountCents = firstDefined(payload?.amount_cents, item?.amount_cents);
  const decayDate = firstDefined(payload?.decay_date, item?.decay_date);
  const underRule = firstDefined(item?.under_rule, payload?.under_rule);
  const senderKnown = Object.prototype.hasOwnProperty.call(payload, "sender_known") ? payload.sender_known : null;
  const senderEmail = firstDefined(payload?.sender_email, item?.sender_email);

  return {
    id: firstDefined(item?.id, item?.event_id),
    classification: classification === "opportunity" ? "opportunity" : "obligation",
    title,
    ownerRaw,
    ownerParty: asOwnerParty(ownerRaw),
    ownerLabel: firstDefined(item?.owner_label, payload?.owner_label),
    counterparty,
    gateDate,
    amountCents,
    amount: amountCents === undefined || amountCents === null || amountCents === "" ? null : Number(amountCents) / 100,
    decayDate,
    senderKnown,
    senderEmail: senderEmail || null,
    ts: firstDefined(item?.ts, item?.created_at),
    evidenceRefs: listFrom(item, ["evidence_refs", "references", "attachments"]),
    underRule,
    sourceLine: feederOriginLabel(underRule),
    provenance: {
      channel: "feeder",
      origin: feederOriginLabel(underRule),
      timestamp: firstDefined(item?.ts, item?.created_at),
      feederRule: underRule,
    },
    payload,
    raw: item,
  };
};

export const partitionCandidates = (candidates = []) => {
  const desk = [];
  const quarantine = [];
  for (const candidate of candidates) {
    if (candidate?.senderKnown === false) quarantine.push(candidate);
    else desk.push(candidate);
  }
  return { desk, quarantine };
};

const normalizeReviewItem = (item, kind) => ({
  id: firstDefined(item?.id, item?.suggestion_id, item?.capture_id),
  kind,
  title: firstDefined(item?.title, item?.subject, item?.headline, item?.raw_text, kind === "capture" ? "Parsed from your note" : "Suggested obligation"),
  subtitle: firstDefined(item?.subtitle, item?.summary),
  ownerParty: asOwnerParty(firstDefined(item?.owner_party, item?.owner, "joint")),
  ownerLabel: firstDefined(item?.owner_label),
  amount: firstDefined(item?.amount, item?.estimated_amount),
  dueDate: firstDefined(item?.due_date, item?.deadline),
  counterparty: firstDefined(item?.counterparty, item?.contact_name, item?.from),
  asset: firstDefined(item?.asset_label, item?.asset_name, item?.property_name, item?.address),
  rawText: firstDefined(item?.raw_text, item?.capture_raw_text, item?.notes),
  provenance: normalizeProvenance(item),
  status: statusMeta(kind === "suggestion" ? "handled" : "needs_you", kind === "suggestion" ? "Suggested discharge" : "Parsed from your note"),
  raw: item,
});

const request = (method, url, hid, data, config = {}) =>
  api.request({
    method,
    url,
    data,
    ...config,
    params: {
      household_id: hid,
      ...(config.params || {}),
    },
  });

const postFirstAvailable = async (hid, urls, payload) => {
  let lastError = null;
  for (const url of urls) {
    try {
      return await request("post", url, hid, payload);
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      if (![404, 405].includes(status)) throw error;
    }
  }
  throw lastError;
};

export async function listObligations(hid, options = {}) {
  const response = await request("get", "/obligations", hid, null, { params: options });
  const payload = response?.data;
  // Canonical API (OA-205 R1): GET /obligations returns a BARE ARRAY of
  // projection rows. Feed it ONLY into obligations — captureReview and
  // opportunities have their own sources (capture responses / later rounds);
  // letting the array fall through listFrom for those keys duplicated every
  // obligation into the review tray and the opportunities rail.
  const isArray = Array.isArray(payload);
  return {
    obligations: (isArray ? payload : listFrom(payload, ["obligations", "items", "rows", "results"])).map(normalizeObligation),
    captureReview: (isArray ? [] : listFrom(payload, ["capture_review", "capture_candidates", "captures_to_confirm", "pending_captures"])).map((item) => normalizeReviewItem(item, "capture")),
    filedTodayCount: firstDefined(payload?.filed_today_count, payload?.filed_quietly_today, 0) || 0,
    filedTodayNote: firstDefined(payload?.filed_today_note, payload?.quiet_note),
    opportunities: (isArray ? [] : listFrom(payload, ["opportunities"])).map((item) => normalizeReviewItem(item, "opportunity")),
    raw: payload,
  };
}

export async function createObligation(hid, payload) {
  const response = await request("post", "/obligations", hid, payload);
  return normalizeObligation(response?.data || {});
}

// The API's ActionIn literal is snake_case (reply_sent, mark_handled, ...);
// UI action ids are kebab-case. Normalize at the API boundary — a kebab id
// reaching the wire is a guaranteed 422 ("Action did not stick").
const toApiAction = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

export async function listCandidates(hid, options = {}) {
  const params = options.lane ? { lane: options.lane } : {};
  const response = await request("get", "/obligations/candidates", hid, null, { params });
  const payload = response?.data;
  return (Array.isArray(payload) ? payload : listFrom(payload, ["candidates", "items", "results"])).map(normalizeCandidate);
}

export async function promoteCandidate(hid, candidateId) {
  const response = await request("post", `/obligations/candidates/${candidateId}/promote`, hid, undefined);
  return response?.data;
}

export async function acceptCandidate(hid, candidate, overrides = {}) {
  const original = normalizeCandidate(candidate?.raw || candidate);
  const nextTitle = firstDefined(overrides.title, original.title);
  const nextOwner = firstDefined(overrides.owner, original.ownerRaw);
  const nextCounterparty = firstDefined(overrides.counterparty, original.counterparty);
  const nextGateDate = overrides.gate_date !== undefined ? overrides.gate_date : original.gateDate;
  const nextAmountCents = overrides.amount_cents !== undefined ? overrides.amount_cents : original.amountCents;

  const body = {};
  if (nextTitle !== original.title) body.title = nextTitle;
  if (nextOwner !== original.ownerRaw) body.owner = nextOwner;
  if (nextCounterparty !== original.counterparty) body.counterparty = nextCounterparty;
  if (nextGateDate !== original.gateDate && nextGateDate) body.gate_date = nextGateDate;
  if (nextAmountCents !== original.amountCents) body.amount_cents = nextAmountCents;
  if (overrides.asset_id !== undefined) body.asset_id = overrides.asset_id;

  const response = await request("post", `/obligations/candidates/${original.id}/accept`, hid, body);
  return normalizeObligation(response?.data || {});
}

export async function dismissCandidate(hid, candidate) {
  const candidateId = firstDefined(candidate?.id, candidate?.raw?.id);
  const response = await request("post", `/obligations/candidates/${candidateId}/dismiss`, hid, undefined);
  return response?.data;
}

export async function actionObligation(hid, id, action, payload = {}) {
  // STRICT body (pydantic extra=forbid): only action + per-action requirements.
  const body = { action: toApiAction(action) };
  if (payload.gate_date) body.gate_date = payload.gate_date;
  if (payload.into_id) body.into_id = payload.into_id;
  if (payload.evidence_refs) body.evidence_refs = payload.evidence_refs;
  const response = await request("post", `/obligations/${id}/action`, hid, body);
  return response?.data;
}

export async function captureObligation(hid, text) {
  const response = await request("post", "/obligations/capture", hid, { text });
  const payload = response?.data;
  // Canonical API shape (OA-205 R2a): { proposal: {title, owner, counterparty, gate_date, degraded?}, capture_event_id }
  if (payload?.capture_event_id) {
    return [normalizeReviewItem({
      id: payload.capture_event_id,
      capture_id: payload.capture_event_id,
      title: payload?.proposal?.title,
      owner: payload?.proposal?.owner,
      counterparty: payload?.proposal?.counterparty,
      gate_date: payload?.proposal?.gate_date,
      degraded: payload?.proposal?.degraded,
      raw_text: text,
    }, "capture")];
  }
  const items = listFrom(payload, ["capture_review", "captures", "items", "results"]);
  if (items.length) return items.map((item) => normalizeReviewItem(item, "capture"));
  if (payload && typeof payload === "object") return [normalizeReviewItem(payload, "capture")];
  return [];
}

export async function confirmCapture(hid, item, overrides = {}) {
  // Canonical endpoint (OA-205 R2a): POST /obligations/capture/{capture_event_id}/confirm
  // Body is STRICT (pydantic extra=forbid): only {title, owner, counterparty?, gate_date?}.
  const captureId = firstDefined(item?.raw?.capture_id, item?.captureId, item?.id);
  const body = {
    title: firstDefined(overrides.title, item?.title, item?.rawText, "Untitled obligation"),
    owner: firstDefined(overrides.owner, item?.raw?.owner, "david"),
  };
  const counterparty = firstDefined(overrides.counterparty, item?.raw?.counterparty);
  if (counterparty) body.counterparty = counterparty;
  const gateDate = firstDefined(overrides.gate_date, item?.raw?.gate_date);
  if (gateDate) body.gate_date = gateDate;
  const response = await request("post", `/obligations/capture/${captureId}/confirm`, hid, body);
  return response?.data;
}

export async function listSuggestions(hid) {
  try {
    const response = await request("get", "/obligations/suggestions", hid, null);
    return listFrom(response?.data, ["suggestions", "items", "rows", "results"]).map((item) => normalizeReviewItem(item, "suggestion"));
  } catch (error) {
    if ([404, 405].includes(error?.response?.status)) return [];
    throw error;
  }
}

export async function confirmSuggestion(hid, item, overrides = {}) {
  const payload = {
    suggestion_id: firstDefined(item?.id, item?.raw?.suggestion_id, item?.raw?.id),
    id: firstDefined(item?.id, item?.raw?.suggestion_id, item?.raw?.id),
    ...overrides,
  };
  const response = await postFirstAvailable(hid, [
    `/obligations/suggestions/${payload.suggestion_id}/confirm`,
    "/obligations/suggestions-confirm",
    "/obligations/suggestions/confirm",
  ], payload);
  return response?.data;
}

export async function rejectSuggestion(hid, item) {
  const payload = {
    suggestion_id: firstDefined(item?.id, item?.raw?.suggestion_id, item?.raw?.id),
    id: firstDefined(item?.id, item?.raw?.suggestion_id, item?.raw?.id),
  };
  const response = await postFirstAvailable(hid, [
    `/obligations/suggestions/${payload.suggestion_id}/reject`,
    "/obligations/suggestions-reject",
    "/obligations/suggestions/reject",
  ], payload);
  return response?.data;
}

export async function getObligationEvents(hid, id) {
  try {
    const response = await request("get", `/obligations/${id}/events`, hid, null);
    const payload = response?.data;
    const events = (Array.isArray(payload) ? payload : listFrom(payload, ["events", "items", "history"])).map((event) => normalizeEvent(event));
    return events.sort((a, b) => {
      const aSeq = a?.seq;
      const bSeq = b?.seq;
      if (aSeq !== null && aSeq !== undefined && bSeq !== null && bSeq !== undefined && aSeq !== bSeq) {
        return aSeq - bSeq;
      }
      const aTime = new Date(a?.timestamp || 0).getTime();
      const bTime = new Date(b?.timestamp || 0).getTime();
      return aTime - bTime;
    });
  } catch (error) {
    if (!error?.response) return [];
    throw error;
  }
}

export const obligationCounts = (obligations = [], reviewItems = []) => {
  const counts = {
    needs_you: 0,
    waiting_on_them: 0,
    gated: 0,
    to_confirm: reviewItems.length,
  };
  for (const obligation of obligations) {
    if (obligation.lane === "needs_you") counts.needs_you += 1;
    if (obligation.lane === "waiting_on_them") counts.waiting_on_them += 1;
    if (obligation.lane === "gated") counts.gated += 1;
  }
  return counts;
};

export const obligationActionLabel = (action) => {
  const labels = {
    "reply-sent": "Reply sent",
    "mark-handled": "Mark handled",
    snooze: "Snooze",
    reopen: "Reopen",
    void: "Void",
    merge: "Merge",
  };
  return labels[action] || action;
};
